import { AlertCircle, CheckCircle2, Circle, Loader2, Lock, type LucideIcon } from "lucide-react";
import type { WorkflowArtefactStatus } from "./context";

interface Props {
  items: WorkflowArtefactStatus[];
}

const PHASE_ORDER = ["define", "source", "assess"] as const;

const PHASE_LABELS: Record<string, string> = {
  define: "Define",
  source: "Source & Deliver",
  assess: "Assess",
};

type StatusTag = "complete" | "generating" | "pending" | "ready" | "blocked";

interface StatusStyle {
  icon: LucideIcon;
  tone: string;
  spin?: boolean;
}

const STATUS_STYLES: Record<StatusTag, StatusStyle> = {
  complete: {
    icon: CheckCircle2,
    tone: "text-emerald-700 dark:text-emerald-400",
  },
  generating: {
    icon: Loader2,
    tone: "text-blue-600 dark:text-blue-400",
    spin: true,
  },
  pending: {
    icon: Circle,
    tone: "text-amber-600 dark:text-amber-400",
  },
  ready: {
    icon: Circle,
    tone: "text-text-faint",
  },
  blocked: {
    icon: AlertCircle,
    tone: "text-text-faint opacity-50",
  },
};

export function WorkflowStatus({ items }: Props) {
  const grouped = groupByPhase(items);

  const totalCount = items.length;
  const completeCount = items.filter((i) => i.status === "complete").length;

  return (
    <section data-testid="workflow-status">
      <header className="mb-3 flex items-baseline justify-between">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-text-muted">
          Campaign Progress
        </h2>
        <span className="text-xs text-text-faint">
          {completeCount}/{totalCount} complete
        </span>
      </header>

      <div className="rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
        {/* Progress bar */}
        <div className="mb-4">
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-zinc-200 dark:bg-zinc-700">
            <div
              className="h-full rounded-full bg-emerald-500 transition-all duration-500"
              style={{ width: `${totalCount > 0 ? (completeCount / totalCount) * 100 : 0}%` }}
            />
          </div>
        </div>

        <div className="space-y-5">
          {PHASE_ORDER.map((phase) => {
            const phaseItems = grouped[phase];
            if (!phaseItems || phaseItems.length === 0) return null;

            return (
              <div key={phase}>
                <h3 className="mb-2 text-xs font-medium text-text-muted">{PHASE_LABELS[phase]}</h3>
                <ul className="space-y-1.5">
                  {phaseItems.map((item) => (
                    <WorkflowItem key={item.kind} item={item} />
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function WorkflowItem({ item }: { item: WorkflowArtefactStatus }) {
  const statusTag = item.status as StatusTag;
  const style = STATUS_STYLES[statusTag] ?? STATUS_STYLES.ready;
  const Icon = style.icon;

  return (
    <li
      className="flex items-start gap-2 text-sm"
      data-workflow-kind={item.kind}
      data-workflow-status={item.status}
    >
      <span className={`mt-0.5 shrink-0 ${style.tone}`}>
        <Icon size={14} className={style.spin ? "animate-spin" : ""} />
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span
            className={`font-medium ${item.status === "blocked" ? "text-text-faint" : "text-text-primary"}`}
          >
            {item.label}
          </span>
          {item.locked && (
            <Lock size={11} className="shrink-0 text-emerald-600 dark:text-emerald-400" />
          )}
          {item.status === "generating" && (
            <span className="text-xs text-blue-600 dark:text-blue-400">Generating...</span>
          )}
        </div>
        {item.blockedReason && item.status === "blocked" && (
          <p className="text-xs text-text-faint">{item.blockedReason}</p>
        )}
      </div>
    </li>
  );
}

function groupByPhase(items: WorkflowArtefactStatus[]): Record<string, WorkflowArtefactStatus[]> {
  const result: Record<string, WorkflowArtefactStatus[]> = {};
  for (const item of items) {
    const phase = item.phase;
    if (!result[phase]) result[phase] = [];
    result[phase].push(item);
  }
  return result;
}
